import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  ClipboardList,
  Home,
  Heart,
  Leaf,
  Lock,
  PackageCheck,
  Plus,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Tag,
  Trash2,
  Truck,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DecorLeaf from '../components/DecorLeaf';
import QuantityStepper from '../components/QuantityStepper';
import ProductImage from '../components/ProductImage';
import { SkeletonBlock } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import CartCoupon from '../components/CartCoupon';
import QuoteSummary from '../components/QuoteSummary';
import StorefrontServiceabilityGate from '../components/StorefrontServiceabilityGate';
import usePageMetadata from '../hooks/usePageMetadata';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavourites } from '../context/FavouritesContext';
import { useToast } from '../context/ToastContext';
import { useDeliveryLocation } from '../context/DeliveryLocationContext';
import { useCouponIntent } from '../context/CouponIntentContext';
import useCartQuote from '../hooks/useCartQuote';
import { productPriceView } from '../utils/offerPresentation';
import { focusRing, homeRail } from '../styles/ui';

// Rupee amounts come from the backend quote. Before sign-in (the quote needs
// an account) a guest sees the catalogue snapshot the cart cached, labelled as
// such, and never a total. Nothing here invents a fee or a rate.
const formatCurrency = (value) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `₹${Number.isInteger(value) ? value : value.toFixed(2)}`
    : null;

const uomFor = (product) => product?.sellingUom || product?.uom || product?.b2cUnit?.sellingUom || null;

// Brand promises already used across the storefront — no delivery time, no
// certification and no customer count is claimed.
const HERO_POINTS = [
  { icon: Leaf, title: 'Farm Fresh', hint: 'Handpicked daily' },
  { icon: PackageCheck, title: 'Hygienically Packed', hint: "For your family's safety" },
  { icon: ShieldCheck, title: 'Quality Checked', hint: 'Inspected before dispatch' },
  { icon: Truck, title: 'Fast Delivery', hint: 'Right to your doorstep' },
];

const TRUST_STRIP = [
  { icon: Leaf, title: '100% Fresh', hint: 'Straight from farms' },
  { icon: PackageCheck, title: 'Hygienically Packed', hint: "For your family's safety" },
  { icon: Truck, title: 'Fast Delivery', hint: 'At your doorstep' },
  { icon: ShieldCheck, title: 'Quality Checked', hint: 'Assured freshness' },
];

// The payment routes checkout actually offers (see CheckoutPage): no brand
// marks, and nothing this app cannot take.
const PAYMENT_ROUTES = ['Cash on Delivery', 'Wallet', 'Online Payment'];

const primaryCta = `inline-flex items-center justify-center gap-2 rounded-full bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white font-bold shadow-[0_8px_18px_-6px_rgba(46,139,71,0.55)] transition-all hover:shadow-[0_10px_22px_-6px_rgba(46,139,71,0.65)] ${focusRing}`;
const outlinedCta = `inline-flex items-center justify-center gap-2 rounded-full bg-white border border-[#2E8B47] text-[#1E5A30] font-bold hover:bg-emerald-50 transition-colors ${focusRing}`;

// The offer the quote applied to one line, as the quote reports it: its label
// and the rupees it took off that line (and free units, for a multi-buy).
// Explanatory only — the line total beside it is the quote's own figure.
const LineOffer = ({ offer }) => {
  const saved = offer.discountAmount > 0 ? formatCurrency(offer.discountAmount) : null;
  return (
    <span
      className="mt-2 inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 max-w-full text-[12px] font-semibold text-accent-700 bg-accent-50 border border-accent-100 px-2.5 py-1 rounded-full"
      data-testid="line-offer"
    >
      <Tag size={12} className="shrink-0" aria-hidden="true" />
      <span className="sr-only">Offer applied: </span>
      <span className="break-words">{offer.label || 'Offer applied'}</span>
      {offer.freeQuantity > 0 && <span className="font-medium">· {offer.freeQuantity} free</span>}
      {saved && <span className="font-medium text-emerald-800">· You save {saved}</span>}
    </span>
  );
};

const Card = ({ children, className = '' }) => (
  <section
    className={`bg-white rounded-[20px] border border-[#E6EEE6] shadow-[0_10px_30px_-16px_rgba(15,51,24,0.25)] ${className}`}
  >
    {children}
  </section>
);

const IconDisc = ({ icon: Icon, className = '' }) => (
  <span
    className={`w-11 h-11 rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0 ${className}`}
  >
    <Icon size={20} strokeWidth={1.9} aria-hidden="true" />
  </span>
);

// ── Hero ──────────────────────────────────────────────────────────────────
// Breadcrumb, serif title, the brand promises and the design's own produce
// crate on the right. Owned local artwork only.
const CartHero = () => (
  <section className="relative overflow-hidden pt-20 bg-[linear-gradient(180deg,#F0F8ED_0%,#F6FAF3_60%,#FFFFFF_100%)]">
    <div className="pointer-events-none select-none absolute inset-x-0 top-20 bottom-0" aria-hidden="true">
      <img
        src="/pages/cart-hero-crate.webp"
        alt=""
        width="451"
        height="238"
        className="hidden lg:block absolute right-0 bottom-0 w-[340px] xl:w-[451px] h-auto"
      />
      <img
        src="/pages/cart-note.webp"
        alt=""
        width="185"
        height="90"
        className="hidden xl:block absolute right-[440px] top-[24px] w-[170px]"
      />
      <DecorLeaf className="hidden sm:block absolute left-[44px] top-[36px] w-9 text-emerald-500/80 -rotate-[20deg]" />
      <DecorLeaf className="hidden lg:block absolute left-[18px] top-[164px] w-12 text-emerald-600/70 rotate-[30deg]" />
      <DecorLeaf className="hidden xl:block absolute left-[700px] top-[40px] w-8 text-emerald-500/70 rotate-[18deg]" />
      <DecorLeaf className="hidden xl:block absolute left-[980px] top-[120px] w-10 text-emerald-600/60 -rotate-[12deg]" />
    </div>

    <div className={`${homeRail} relative`}>
      <div className="pt-[18px] pb-6 xl:pb-[26px] xl:min-h-[238px] lg:pr-[360px] xl:pr-[420px]">
        <nav aria-label="Breadcrumb">
          <ol className="flex items-center gap-1.5 text-[13px] text-ink-secondary">
            <li>
              <Link to="/" className={`inline-flex items-center gap-1.5 hover:text-emerald-700 ${focusRing}`}>
                <Home size={14} aria-hidden="true" />
                Home
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight size={14} />
            </li>
            <li aria-current="page" className="font-semibold text-emerald-950">
              Your Cart
            </li>
          </ol>
        </nav>
        <h1 className="serif mt-[8px] text-[40px] sm:text-[52px] xl:text-[64px] font-bold text-emerald-950 leading-[1]">
          Your Cart
        </h1>
        <p className="mt-[10px] text-[16px] xl:text-[18px] text-[#4B5563]">Fresh choices, one step closer to your home.</p>

        <ul className="mt-[16px] grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4 xl:flex xl:gap-0">
          {HERO_POINTS.map(({ icon: Icon, title, hint }, index) => (
            <li
              key={title}
              className={`flex items-center gap-2.5 xl:pr-5 ${
                index > 0 ? 'xl:pl-5 xl:border-l xl:border-[#D9E6D9]' : ''
              }`}
            >
              <span className="w-10 h-10 rounded-full bg-white border border-[#DCE8DC] text-emerald-700 flex items-center justify-center shrink-0 shadow-[0_4px_10px_-6px_rgba(15,51,24,0.3)]">
                <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-bold text-emerald-950 leading-tight">{title}</span>
                <span className="block text-[11.5px] text-ink-secondary leading-tight mt-[2px] whitespace-nowrap">{hint}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

// ── Trust strip ───────────────────────────────────────────────────────────
const TrustStrip = () => (
  <ul className="mt-8 xl:mt-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-0 rounded-[20px] bg-white border border-[#E6EEE6] shadow-[0_10px_30px_-16px_rgba(15,51,24,0.25)] px-5 py-4 xl:py-5 xl:divide-x xl:divide-[#E6EEE6]">
    {TRUST_STRIP.map(({ icon: Icon, title, hint }) => (
      <li key={title} className="flex items-center gap-3.5 xl:px-6 first:xl:pl-2">
        <span className="w-12 h-12 rounded-full bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0">
          <Icon size={22} strokeWidth={1.8} aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block text-[15px] font-bold text-emerald-950 leading-tight">{title}</span>
          <span className="block text-[12.5px] text-ink-secondary leading-tight mt-[3px]">{hint}</span>
        </span>
      </li>
    ))}
  </ul>
);

// ── Favourites rail ───────────────────────────────────────────────────────
// The app has no recommendation engine — only Favourites — so this shelf is
// labelled for what it actually is, and reads FavouritesContext directly.
// Price, struck price and stock are the catalogue's own projection.
const FavouritesRail = ({ products, total, onAdd }) => {
  if (!products.length) return null;
  return (
    <Card className="p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <IconDisc icon={Heart} />
          <div className="min-w-0">
            <h2 className="serif text-[20px] sm:text-[22px] font-bold text-emerald-950 leading-none">From your Favourites</h2>
            <p className="text-[12.5px] text-ink-secondary mt-[5px]">Items you saved — add them to complete your order.</p>
          </div>
        </div>
        <Link
          to="/favourites"
          className={`inline-flex items-center gap-1.5 h-[36px] px-4 rounded-full border border-[#D7E7D7] bg-white text-[13px] font-bold text-emerald-900 hover:bg-emerald-50 shrink-0 ${focusRing}`}
        >
          View All ({total})
          <ArrowRight size={14} aria-hidden="true" />
        </Link>
      </div>

      {/* As many equal columns as fit (five at the reference width); fewer
          favourites simply get wider cards instead of an empty slot. */}
      <ul className="grid grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(168px,1fr))] lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {products.map((product) => {
          const uom = uomFor(product);
          const { price, compareAtPrice } = productPriceView(product);
          return (
            <li key={product._id} className="flex flex-col rounded-2xl border border-[#E6EEE6] bg-[#FBFDFB] overflow-hidden">
              <Link
                to={`/products/${product.sku || product._id}`}
                className={`block aspect-[4/3] max-h-[150px] w-full bg-[#F4F7F3] overflow-hidden ${focusRing}`}
                aria-label={product.displayName}
              >
                <ProductImage src={product.thumbnail} alt="" />

              </Link>
              <div className="flex flex-col flex-1 p-3">
                <Link
                  to={`/products/${product.sku || product._id}`}
                  className={`block text-[13.5px] font-bold text-emerald-950 leading-snug line-clamp-1 ${focusRing}`}
                >
                  {product.displayName}
                </Link>
                {uom && <span className="block text-[11px] text-ink-secondary mt-[3px]">{uom}</span>}
                <div className="mt-auto pt-2.5 flex items-center justify-between gap-1.5">
                  <span className="flex flex-wrap items-baseline gap-x-1.5 min-w-0">
                    <span className="text-[14.5px] font-bold text-emerald-950">{formatCurrency(price) || '—'}</span>
                    {compareAtPrice !== null && (
                      <span className="text-[11px] text-gray-400 line-through">{formatCurrency(compareAtPrice)}</span>
                    )}
                  </span>
                  {/* Out-of-stock favourites expose no working add control. */}
                  {product.isInStock ? (
                    <button
                      type="button"
                      onClick={() => onAdd(product)}
                      aria-label={`Add ${product.displayName} to cart`}
                      className={`${primaryCta} h-[30px] px-2.5 text-[11.5px] gap-1 shrink-0`}
                    >
                      Add
                      <Plus size={13} strokeWidth={2.5} aria-hidden="true" />
                    </button>
                  ) : (
                    <span className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-100 rounded-full px-2.5 py-1 shrink-0">
                      Out of stock
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

const CartPage = () => {
  const { items, setQuantity, removeItem, clearCart, addItem } = useCart();
  const { authStatus } = useAuth();
  const { favouriteProducts, favouriteCount } = useFavourites();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { storefrontStatus } = useDeliveryLocation();
  // The coupon code the customer APPLIED (not what they are typing): part of
  // the quote's identity, kept across cart and location changes — and carried
  // to Checkout — until they remove it. Whether it applies is only ever read
  // from a quote.
  const { couponCode: couponIntent, setCouponCode: setCouponIntent, clearCouponCode } = useCouponIntent();
  // The backend's price for exactly this cart, at exactly this location, with
  // exactly this coupon; see hooks/useCartQuote. Its quoteToken stays there
  // for checkout, untouched here.
  const {
    status: quoteStatus,
    quote,
    error: quoteFailure,
    refresh: retryQuote,
    couponCode: submittedCoupon,
    canQuote,
  } = useCartQuote({ couponCode: couponIntent });
  const loadingQuote = quoteStatus === 'loading';
  const quoteError = quoteFailure ? quoteFailure.message || 'Please try again.' : null;
  const storefrontReady = storefrontStatus === 'ready';
  const isGuest = authStatus !== 'authenticated';

  usePageMetadata({
    title: 'Your Cart | Fresh & Homely',
    description: 'Review the items in your Fresh & Homely cart before checkout.',
    canonicalPath: '/cart',
  });

  const issueFor = (productId) => quote?.issues?.find((issue) => String(issue.productId) === String(productId));
  // Basket-wide issues (e.g. location_required) carry no productId. A refused
  // coupon is explained in the coupon section instead, so it is not repeated.
  const cartIssues = (quote?.issues || []).filter((issue) => !issue.productId && issue.type !== 'coupon_invalid');

  // Applying the code already submitted asks again (e.g. after a failure);
  // any other code becomes the new quote input.
  const handleApplyCoupon = (code) => {
    if (code === submittedCoupon) retryQuote();
    else setCouponIntent(code);
  };
  const handleRemoveCoupon = () => clearCouponCode();
  const quoteLineFor = (productId) =>
    quote?.items?.find((line) => String(line.productId) === String(productId));

  const handleRemove = (item) => {
    removeItem(item.productId);
    showToast(`${item.product?.displayName || 'Item'} removed from cart`, { tone: 'info' });
  };

  const handleClearCart = () => {
    clearCart();
    showToast('Cart cleared', { tone: 'info' });
  };

  const handleAddFavourite = (product) => {
    addItem(product);
    showToast(`${product.displayName} added to cart`);
  };

  // Checkout only opens on a price the backend has confirmed for this cart at
  // this location. A line the outlet cannot supply in full (removed, or priced
  // for fewer units than asked) must be corrected first, because checkout
  // places the cart as it stands. Guests still sign in first, as before.
  const lineProblems = (quote?.issues || []).filter((issue) => issue.productId);
  const blockedByLocation = (quote?.issues || []).some((issue) => issue.type === 'location_required');
  const unpricedLines = quote ? items.filter((item) => !quoteLineFor(item.productId) && !issueFor(item.productId)) : [];
  let checkoutBlocker = null;
  if (!storefrontReady) checkoutBlocker = 'Choose a delivery location we serve to continue.';
  else if (isGuest) checkoutBlocker = null;
  else if (quoteError) checkoutBlocker = "We couldn't confirm your cart price yet.";
  else if (!quote) checkoutBlocker = 'Confirming your cart price…';
  else if (blockedByLocation) checkoutBlocker = 'Choose a delivery location we serve to continue.';
  else if (lineProblems.length > 0 || unpricedLines.length > 0) checkoutBlocker = 'Update the highlighted items to continue.';
  const canCheckout = !checkoutBlocker;

  const handleCheckout = () => {
    if (!canCheckout) return;
    if (isGuest) {
      navigate('/login?redirect=/checkout');
      return;
    }
    navigate('/checkout');
  };

  // One row of five on the widest layout; the rest live on /favourites.
  const favouritesForRail = useMemo(
    () => (Array.isArray(favouriteProducts) ? favouriteProducts.slice(0, 5) : []),
    [favouriteProducts]
  );

  const distinctCount = items.length;
  const hasQuote = Boolean(quote);
  const countLine = `${distinctCount} item${distinctCount === 1 ? '' : 's'} in your cart`;

  if (items.length === 0) {
    return (
      <>
        <Navbar />
        <main>
          <CartHero />
          <section className="pt-6 sm:pt-8 pb-10 sm:pb-14 bg-[#F3F9F1]">
            <div className={homeRail}>
              {/* No Order Summary is rendered for an empty cart. */}
              <EmptyState
                icon={ShoppingCart}
                title="Your cart is empty"
                description="Browse our categories to find fresh products."
                action={{ label: 'Shop Categories', to: '/categories' }}
              />
              <div className="mt-5 xl:mt-6">
                <FavouritesRail
                  products={favouritesForRail}
                  total={favouriteCount}
                  onAdd={handleAddFavourite}
                />
              </div>
              <TrustStrip />
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <CartHero />

        <section className="relative overflow-hidden pt-6 sm:pt-7 pb-10 sm:pb-14 bg-[#F3F9F1]">
          <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
            <DecorLeaf className="hidden xl:block absolute left-[18px] bottom-[60px] w-12 text-emerald-600/60 -rotate-[24deg]" />
            <DecorLeaf className="hidden xl:block absolute right-[22px] bottom-[40px] w-14 text-emerald-500/60 rotate-[18deg]" />
          </div>

          <div className={`${homeRail} relative`}>
            {/* Three grid items so the DOM order — items, summary, favourites —
                is also the single-column order (the total and Proceed button
                come before the shelf), while on two columns the summary spans
                both rows on the right. */}
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,71fr)_minmax(340px,29fr)] gap-5 xl:gap-6 items-start">
              {/* ── Cart items ─────────────────────────────────────────── */}
              <div className="min-w-0 lg:col-start-1 lg:row-start-1">
                <Card>
                  <div className="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-[#EEF3EE]">
                    {/* No selection checkboxes: every cart item always goes to
                        checkout, so a per-item toggle would be decorative. */}
                    <div className="flex items-center gap-3">
                      <IconDisc icon={ShoppingCart} />
                      <div className="min-w-0">
                        <h2 className="serif text-[22px] sm:text-[26px] font-bold text-emerald-950 leading-none">
                          Cart Items <span className="text-emerald-700">({distinctCount})</span>
                        </h2>
                        <p className="text-[12.5px] text-ink-secondary mt-[5px]">{countLine}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                      {/* clearCart is a real CartContext operation. */}
                      <button
                        type="button"
                        onClick={handleClearCart}
                        className={`inline-flex items-center gap-1.5 h-[40px] px-3 rounded-full text-[13.5px] font-semibold text-[#4B5563] hover:text-red-700 hover:bg-red-50 transition-colors ${focusRing}`}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                        Clear Cart
                      </button>
                      <Link
                        to="/categories"
                        className={`hidden sm:inline-flex items-center gap-2 h-[40px] px-4 rounded-full border border-[#D7E7D7] bg-white text-[13.5px] font-bold text-emerald-900 hover:bg-emerald-50 transition-colors ${focusRing}`}
                      >
                        <ArrowLeft size={15} aria-hidden="true" />
                        Back to Shopping
                      </Link>
                    </div>
                  </div>

                  <ul className="px-5 sm:px-6 divide-y divide-[#EEF3EE]" data-testid="cart-items">
                    {items.map((item) => {
                      const product = item.product;
                      const issue = issueFor(item.productId);
                      const line = quoteLineFor(item.productId);
                      const uom = uomFor(product);
                      // Only two sources of a rupee figure: the backend quote
                      // (signed in), or, for a guest who cannot be quoted, the
                      // catalogue snapshot, labelled as such below. Otherwise
                      // no amount is shown rather than a stale one.
                      const showCatalogue = isGuest && storefrontReady;
                      const unitPrice = quote ? line?.unitPrice : showCatalogue ? product?.price : null;
                      const lineTotal = quote
                        ? line?.lineTotal
                        : showCatalogue && typeof product?.price === 'number'
                        ? product.price * item.quantity
                        : null;
                      const unpriced = Boolean(quote) && !line && !issue;
                      return (
                        <li key={item.productId} className="py-4">
                          {/* One geometry for every row. Wide: IMAGE | DETAILS |
                              UNIT PRICE | QUANTITY | TOTAL | REMOVE as fixed
                              columns. Narrow: image + details, then a row with
                              prices on the left and the stepper + remove on the
                              right. Name length never moves the numbers. */}
                          <div className="grid grid-cols-[76px_minmax(0,1fr)] sm:grid-cols-[96px_minmax(0,1fr)] xl:grid-cols-[100px_minmax(0,1fr)_104px_112px_88px_40px] gap-x-4 xl:gap-x-3 gap-y-3 items-center">
                            <span className="w-[76px] h-[76px] sm:w-[96px] sm:h-[96px] xl:w-[100px] xl:h-[100px] rounded-2xl overflow-hidden bg-[#F4F7F3] border border-[#E6EEE6] self-start xl:self-center">
                              <ProductImage src={product?.thumbnail} alt="" size="thumb" />
                            </span>

                            <div className="min-w-0">
                              <Link
                                to={`/products/${product?.sku || item.productId}`}
                                title={product?.displayName || ''}
                                className={`block text-[15px] sm:text-[16px] font-bold text-emerald-950 leading-snug line-clamp-2 ${focusRing}`}
                              >
                                {product?.displayName || 'Product'}
                              </Link>
                              {/* UOM only when the product actually carries one. */}
                              {uom && (
                                <span className="inline-block mt-1 text-[11px] font-semibold text-emerald-800 bg-[#E4F3E5] rounded-full px-2.5 py-[3px]">
                                  {uom}
                                </span>
                              )}
                              {product?.shortDescription && (
                                <p className="text-[12.5px] text-ink-secondary mt-1 truncate">{product.shortDescription}</p>
                              )}
                              {/* Only the quote says which offer a line got. */}
                              {line?.appliedOffer && <LineOffer offer={line.appliedOffer} />}
                              {issue && <span className="block text-[12.5px] text-amber-700 mt-1.5">{issue.message}</span>}
                              {unpriced && (
                                <span className="block text-[12.5px] text-amber-700 mt-1.5">
                                  We couldn&apos;t price this item. Remove it or try again.
                                </span>
                              )}
                            </div>

                            {/* Narrow: its own full-width row. Wide: its children
                                become the four fixed grid columns. */}
                            <div className="col-span-2 xl:contents flex items-center justify-between gap-3">
                              {/* Unit price: the quote's (or, for a guest, the
                                  catalogue snapshot with its MRP). */}
                              <span className="flex flex-col xl:items-end min-w-0">
                                <span className="text-[11px] uppercase tracking-wide text-ink-secondary xl:hidden">Price</span>
                                <span className="flex flex-wrap items-baseline gap-x-1.5 xl:justify-end">
                                  <span className="text-[16px] font-bold text-emerald-950">{formatCurrency(unitPrice) || '—'}</span>
                                  {/* MRP and discount come from the catalogue snapshot,
                                      so they only accompany the snapshot price. */}
                                  {showCatalogue && !quote && typeof product?.mrp === 'number' && product.mrp > product.price && (
                                    <span className="text-[12.5px] text-gray-400 line-through">{formatCurrency(product.mrp)}</span>
                                  )}
                                </span>
                                {showCatalogue && !quote && product?.discountPercentage > 0 && (
                                  <span className="mt-0.5 self-start xl:self-end text-[10.5px] font-bold text-accent-700 bg-accent-50 border border-accent-100 px-1.5 py-px rounded-full">
                                    {product.discountPercentage}% OFF
                                  </span>
                                )}
                              </span>

                              <span className="flex justify-center order-last xl:order-none ml-auto xl:ml-0">
                                <QuantityStepper quantity={item.quantity} onChange={(qty) => setQuantity(item.productId, qty)} />
                              </span>

                              <span className="flex flex-col items-end xl:items-end min-w-[4.5rem]">
                                <span className="text-[11px] uppercase tracking-wide text-ink-secondary xl:hidden">Total</span>
                                <span className="text-[17px] sm:text-[18px] font-extrabold text-emerald-950 whitespace-nowrap">
                                  {loadingQuote ? <SkeletonBlock className="h-6 w-16" /> : formatCurrency(lineTotal) || '—'}
                                </span>
                              </span>

                              <button
                                type="button"
                                onClick={() => handleRemove(item)}
                                aria-label={`Remove ${product?.displayName || 'item'} from cart`}
                                title="Remove"
                                className={`order-last w-10 h-10 rounded-full border border-transparent text-[#6B7280] hover:text-red-600 hover:bg-red-50 hover:border-red-100 transition-colors flex items-center justify-center shrink-0 ${focusRing}`}
                              >
                                <Trash2 size={18} aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  {!hasQuote && isGuest && storefrontReady && (
                    <p className="text-[13px] text-ink-secondary mx-5 sm:mx-6 mb-5 pt-4 border-t border-[#EEF3EE]">
                      Amounts shown come from the catalogue. Sign in to see final pricing, offers and
                      delivery charges for your address.
                    </p>
                  )}
                  {(hasQuote || !isGuest || !storefrontReady) && <div className="h-1" />}
                </Card>
              </div>

              {/* ── Order summary ──────────────────────────────────────── */}
              <div className="min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 space-y-4">
                <Card className="p-5">
                  <h2 className="flex items-center gap-3 serif text-[21px] sm:text-[22px] font-bold text-emerald-950 mb-4">
                    <IconDisc icon={ClipboardList} />
                    Order Summary
                  </h2>

                  {/* Prices depend on the browsing location: say what is
                      missing instead of showing a total that is not real. */}
                  {!storefrontReady && <StorefrontServiceabilityGate className="mb-5" />}

                  {storefrontReady && isGuest && (
                    <p className="text-[13.5px] text-ink-secondary mb-5">
                      Sign in to see accurate pricing and delivery fees.
                    </p>
                  )}

                  {loadingQuote && (
                    <div className="space-y-3 mb-5" data-testid="summary-skeleton">
                      <SkeletonBlock className="h-4 w-full" />
                      <SkeletonBlock className="h-4 w-full" />
                      <SkeletonBlock className="h-4 w-full" />
                      <SkeletonBlock className="h-8 w-full" />
                    </div>
                  )}

                  {quoteError && (
                    <div
                      role="alert"
                      className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5"
                    >
                      <p className="font-semibold">We couldn&apos;t refresh your cart price.</p>
                      <p className="mt-0.5">{quoteError}</p>
                      <button type="button" onClick={retryQuote} className={`mt-2 font-semibold underline ${focusRing}`}>
                        Try again
                      </button>
                    </div>
                  )}

                  {cartIssues.map((issue) => (
                    <p
                      key={issue.type || issue.message}
                      className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5"
                    >
                      {issue.message}
                    </p>
                  ))}

                  {/* Coupon entry once the cart can be priced here (or a code is
                      already submitted, so it can be seen and removed). */}
                  {(storefrontReady || submittedCoupon) && (
                    <CartCoupon
                      submittedCode={submittedCoupon}
                      quote={quote}
                      quoteStatus={quoteStatus}
                      canApply={canQuote}
                      isGuest={isGuest}
                      onApply={handleApplyCoupon}
                      onRemove={handleRemoveCoupon}
                    />
                  )}

                  {/* Every figure is the quote's own; shared with Checkout. */}
                  {quote && <QuoteSummary quote={quote} itemCount={distinctCount} couponCode={submittedCoupon} cartItems={items} />}

                  {/* The quote says outright whether it is binding. */}
                  {quote && quote.binding === false && (
                    <p className="text-xs text-ink-secondary mb-5 -mt-2">
                      Indicative pricing. Final offers and delivery charges are confirmed once your
                      delivery location is set.
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={handleCheckout}
                    disabled={!canCheckout}
                    aria-describedby={checkoutBlocker ? 'checkout-blocker' : undefined}
                    className={`w-full ${primaryCta} h-[52px] px-6 text-[16px] disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none`}
                  >
                    <Lock size={17} aria-hidden="true" />
                    Proceed to Checkout
                    <ArrowRight size={18} aria-hidden="true" />
                  </button>
                  {checkoutBlocker && (
                    <p id="checkout-blocker" className="text-xs text-ink-secondary text-center mt-2">
                      {checkoutBlocker}
                    </p>
                  )}

                  <Link to="/categories" className={`mt-3 w-full ${outlinedCta} h-[48px] px-6 text-[15px]`}>
                    <ShoppingBag size={18} aria-hidden="true" />
                    Continue Shopping
                  </Link>
                </Card>

                {/* Truthful: these are the routes checkout actually offers.
                    No payment-brand marks are claimed. */}
                <Card className="px-5 py-4">
                  <div className="flex items-center gap-2.5">
                    <ShieldCheck size={18} className="text-emerald-700 shrink-0" aria-hidden="true" />
                    <h2 className="serif text-[17px] font-bold text-emerald-950 leading-tight">Secure Checkout</h2>
                  </div>
                  <ul className="mt-2.5 flex flex-wrap gap-1.5" aria-label="Payment options">
                    {PAYMENT_ROUTES.map((route) => (
                      <li
                        key={route}
                        className="inline-flex items-center h-[28px] px-2.5 rounded-lg border border-[#E6EEE6] bg-[#FBFDFB] text-[11.5px] font-bold text-emerald-950"
                      >
                        {route}
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>

              {favouritesForRail.length > 0 && (
              <div className="min-w-0 lg:col-start-1 lg:row-start-2">
                <FavouritesRail
                  products={favouritesForRail}
                  total={favouriteCount}
                  onAdd={handleAddFavourite}
                />
              </div>
              )}
            </div>

            <TrustStrip />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CartPage;
